import { useParams, Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { Trash2, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  useMonthlyBudget,
  useMonthlyBudgetItems,
  useAddMonthlyBudgetItem,
  useDeleteMonthlyBudgetItem,
} from "@/hooks/useMonthlyBudgets";
import { useMasterBudget } from "@/hooks/useMasterBudgets";
import { useFetchCategories } from "@metadataHooks";
import { BudgetItemFormRow } from "@/pages/budgets/components/BudgetItemFormRow";
import { EditBudgetDialog } from "@/pages/budgets/components/EditBudgetDialog";
import type { BudgetItemInput } from "@/pages/budgets/components/budgetItemSchema";
import {SubCategory} from "@/integrations/supabase/category.models.ts";

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function useAllSubCategories() {
  return useQuery({
    queryKey: ["sub_categories_all"],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("sub_categories")
        .select("*")
        .order("name");
      if (error) throw error;
      return data as SubCategory[];
    },
  });
}

export default function MonthlyBudgetDetailPage() {
  const { id } = useParams<{ id: string }>();

  const { data: budget, isLoading: budgetLoading } = useMonthlyBudget(id);
  const { data: items = [], isLoading: itemsLoading } = useMonthlyBudgetItems(id);
  const { data: masterBudget } = useMasterBudget(budget?.master_budget_id);
  const { data: categories = [] } = useFetchCategories();
  const { data: subCategories = [] } = useAllSubCategories();

  const addItem = useAddMonthlyBudgetItem();
  const deleteItem = useDeleteMonthlyBudgetItem();

  const isLoading = budgetLoading || itemsLoading;

  const getCategoryName = (categoryId: string) =>
    categories.find((c) => c.id === categoryId)?.name ?? categoryId;

  const getSubCategoryName = (subCategoryId: string) =>
    subCategories.find((s) => s.id === subCategoryId)?.name ?? subCategoryId;

  const handleAddItem = async (item: BudgetItemInput) => {
    await addItem.mutateAsync({
      monthly_budget_id: id!,
      ...item,
    });
  };

  const handleDeleteItem = (itemId: string) => {
    deleteItem.mutate({ id: itemId, monthlyBudgetId: id! });
  };

  // ── Loading state ─────────────────────────────────────────────────────────
  if (isLoading) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl space-y-4">
        <Skeleton className="h-8 w-64" />
        <Skeleton className="h-6 w-40" />
        <Skeleton className="h-48 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  // ── Not-found state ───────────────────────────────────────────────────────
  if (!budget) {
    return (
      <div className="container mx-auto py-6 px-4 max-w-4xl">
        <Card>
          <CardContent className="flex flex-col items-center justify-center py-16 gap-4">
            <BookOpen className="h-12 w-12 text-muted-foreground" />
            <p className="text-lg font-medium">Monthly budget not found</p>
            <p className="text-sm text-muted-foreground">
              The budget you're looking for doesn't exist or you don't have access to it.
            </p>
            <Button asChild variant="outline">
              <Link to="/budgets">Back to Budgets</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  const monthName = MONTH_NAMES[budget.month - 1] ?? String(budget.month);

  return (
    <div className="container mx-auto py-6 px-4 max-w-4xl space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="space-y-1">
          <div className="flex items-center gap-2 flex-wrap">
            <h1 className="text-2xl font-bold">{budget.title}</h1>
            <Badge variant="secondary">{monthName} {budget.year}</Badge>
          </div>
          {budget.description && (
            <p className="text-muted-foreground text-sm">{budget.description}</p>
          )}
          {masterBudget && (
            <p className="text-sm text-muted-foreground">
              Master budget:{" "}
              <Link
                to={`/budgets/master/${masterBudget.id}`}
                className="underline underline-offset-2 hover:text-foreground"
              >
                {masterBudget.name}
              </Link>
            </p>
          )}
        </div>

        <div className="shrink-0">
          <EditBudgetDialog
            type="monthly"
            id={budget.id}
            currentName={budget.title}
            currentDescription={budget.description}
            trigger={<Button variant="outline" size="sm">Edit</Button>}
          />
        </div>
      </div>

      {/* Items table */}
      <Card>
        <CardHeader>
          <CardTitle>Budget Items</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {items.length > 0 ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Category</TableHead>
                  <TableHead>Sub-category</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead>Currency</TableHead>
                  <TableHead className="w-12" />
                </TableRow>
              </TableHeader>
              <TableBody>
                {items.map((item) => (
                  <TableRow key={item.id}>
                    <TableCell>{getCategoryName(item.category_id)}</TableCell>
                    <TableCell>{getSubCategoryName(item.sub_category_id)}</TableCell>
                    <TableCell className="text-right font-mono">
                      {item.amount.toLocaleString(undefined, {
                        minimumFractionDigits: 2,
                        maximumFractionDigits: 2,
                      })}
                    </TableCell>
                    <TableCell>{item.currency}</TableCell>
                    <TableCell>
                      <Button
                        variant="ghost"
                        size="icon"
                        className="h-8 w-8 text-destructive hover:text-destructive"
                        onClick={() => handleDeleteItem(item.id)}
                        disabled={deleteItem.isPending}
                        aria-label="Delete item"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <p className="text-sm text-muted-foreground text-center py-6">
              No items yet. Add your first budget item below.
            </p>
          )}

          {/* Add item form */}
          <div className="border-t pt-4">
            <p className="text-sm font-medium mb-3">Add Item</p>
            <BudgetItemFormRow
              onAdd={handleAddItem}
              isAdding={addItem.isPending}
            />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
